import { toast } from 'react-toastify'
import { useState, useCallback } from 'react'
import { patchCPDataWithParams, postCPData, putCPDataWithParams } from 'src/clinic/services'
import { defaultConfirmationModalProps } from 'src/consumer/helpers/utilFunctions'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

/**
 * Custom hook for prescription template save logic
 * Handles API calls, loading state, and confirmation modal
 */
export function usePrescriptionTemplateActions(
  prescriptionTemplate,
  clinicId,
  refreshContent,
  closeDialog,
) {
  const [selectedClinicLocation, setSelectedClinicLocation] = useState(null)
  const [saving, setSaving] = useState(false)
  const [confirmationModalProps, setConfirmationModalProps] = useState(
    defaultConfirmationModalProps,
  )

  // API call to save prescription template
  const savePrescriptionTemplate = useCallback(
    async ({ disableRefreshContent = false, enableSilentRefresh = false } = {}) => {
      setSaving(true)
      try {
        await savePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId })
        if (!disableRefreshContent) {
          refreshContent(undefined, enableSilentRefresh)
        }
      } catch (error) {
        toast.error('Failed to save prescription template')
        console.error('Failed to save prescription template:', error)
        // Handle error - could show a toast/notification here
      } finally {
        setSaving(false)
      }
    },
    [prescriptionTemplate, clinicId, refreshContent],
  )

  async function activatePrescriptionTemplate() {
    setSaving(true)
    try {
      await activatePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId })
      refreshContent()
      closeDialog()
    } catch (error) {
      toast.error('Failed to activate prescription template')
      console.error('Failed to activate prescription template:', error)
      // Handle error - could show a toast/notification here
    } finally {
      setSaving(false)
    }
  }

  async function createPrescriptionTemplate() {
    setSaving(true)
    try {
      const response = await createPrescriptionTemplateApiCall({ clinicId })
      refreshContent()
      closeDialog()
      toast.success('Prescription template created successfully')
      return response
    } catch (error) {
      toast.error('Failed to create prescription template')
      console.error('Failed to create prescription template:', error)
    } finally {
      setSaving(false)
    }
  }

  async function deletePrescriptionTemplate() {
    setSaving(true)
    try {
      await deletePrescriptionTemplateApiCall({
        templateId: prescriptionTemplate?.id,
      })
      refreshContent()
      closeDialog()
      toast.success('Prescription template deleted successfully')
    } catch (error) {
      toast.error('Failed to delete prescription template')
      console.error('Failed to delete prescription template:', error)
    } finally {
      setSaving(false)
    }
  }

  // Handle form submission - show confirmation modal
  const handleSubmit = useCallback(() => {
    setConfirmationModalProps({
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      action: 'save',
      body: 'Do you want to save this prescription pad?',
      buttonColor: 'success',
      onSubmitFunctions: [() => savePrescriptionTemplate({ enableSilentRefresh: true })],
    })
  }, [savePrescriptionTemplate])

  function handleDelete() {
    setConfirmationModalProps({
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      action: 'delete',
      body: 'Are you sure you want to delete this prescription pad? This action cannot be undone.',
      buttonColor: 'danger',
      onSubmitFunctions: [deletePrescriptionTemplate],
    })
  }

  return {
    saving,
    confirmationModalProps,
    handleSubmit,
    activatePrescriptionTemplate,
    createPrescriptionTemplate,
    deletePrescriptionTemplate,
    setSelectedClinicLocation,
    selectedClinicLocation,
    savePrescriptionTemplate,
    handleDelete,
  }
}

export async function savePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId }) {
  const response = await postCPData('/api/secure/doctor/prescription-template/save', {
    ...prescriptionTemplate,
    apiParam: 'prescription-template',
    clinicId: clinicId === 'private' ? null : clinicId,
  })
  if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

async function activatePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId }) {
  const response = await putCPDataWithParams(
    '/api/secure/doctor/prescription-template/activate',
    null,
    {
      'template-id': prescriptionTemplate.id,
    },
  )
  if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
  // try {
  //   const resp = await savePrescriptionTemplateApiCall({
  //     prescriptionTemplate: {
  //       ...prescriptionTemplate,
  //       active: true,
  //     },
  //     clinicId,
  //   })
  //   return Promise.resolve(resp)
  // } catch (error) {
  //   return Promise.reject(error)
  // }
}

async function createPrescriptionTemplateApiCall({ clinicId }) {
  const response = await postCPData('/api/secure/doctor/prescription-template/save', {
    apiParam: 'prescription-template',
    clinicId: clinicId === 'private' ? null : clinicId,
    active: false,
  })
  if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

async function deletePrescriptionTemplateApiCall({ templateId }) {
  const response = await putCPDataWithParams(
    `/api/secure/doctor/prescription/prescription-template/disable`,
    {
      apiParam: 'prescription-template',
    },
    {
      'template-id': templateId,
    },
  )
  if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}
